// Library Methods
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

// Components
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import PackageOrderSection from "../PackageOrderSection/PackageOrderSection";
import DefaultDrawer from "src/components/shared/drawers/DefaultDrawer/DefaultDrawer";
import CustomDatePicker from "src/components/shared/inputs/CustomDatePicker/CustomDatePicker";
import ExamSliderList from "src/components/containers/shared/ExamSliderList/ExamSliderList";
import SimulationSliderList from "../SimulationSliderList/SimulationSliderList";
import VideoSliderList from "../VideoSliderList/VideoSliderList";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { SelectChangeEvent } from "@mui/material/Select";
import { CloseIcon, EyeIcon, PlayIcon, VRIcon } from "src/assets/icons";

// Styles
import {
  ModalCustomConfirm,
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { canUseAnySimulations } from "src/utils/clinic/clinicLicensingHelper";
import { getLocale } from "src/utils/locale/localeHelper";

// Constants
import { priorityLanguageMap } from "src/config/languages";

// Hooks
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

type ExamSliderType = {
  title: string;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ExamSlider = ({
  title,
  isOpen,
  isLoading,
  onClose,
  onConfirm,
}: ExamSliderType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();
  const canUseExamPackaging = clinicInfo?.licensing?.canUseExamPackaging;

  const [selectedTab, setSelectedTab] = useState(0);

  const {
    selectedExamCards,
    unlinkedDeviceOptions,
    shouldDisableDeviceOptions,
    scheduledDate,
    scheduledError,
    selectedDevice,
    actions,
  } = useExamSliderContext();
  const {
    setScheduledDate,
    setScheduledError,
    setSelectedDevice,
    resetValues,
  } = actions;

  useEffect(() => {
    const lensSimCards = selectedExamCards.filter((card) =>
      card?.examFieldType?.includes("simulation")
    );
    const hasOnlySimCards = Boolean(
      lensSimCards.length !== 0 &&
        lensSimCards.length === selectedExamCards.length
    );

    if (isOpen && hasOnlySimCards) {
      setSelectedTab(1);
    }

    const videoCards = selectedExamCards.filter((card) =>
      card?.examFieldType?.includes("video")
    );
    const hasOnlyVideoCards = Boolean(
      videoCards.length !== 0 && videoCards.length === selectedExamCards.length
    );

    if (isOpen && hasOnlyVideoCards) {
      setSelectedTab(2);
    }
  }, [isOpen, selectedExamCards]);

  const dimensions = useComponentDimensions({
    componentId: "#default-drawer-header",
  });

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const tabs = useMemo(
    () => [
      {
        id: 0,
        label: t("Exams"),
        icon: <EyeIcon height="20px" width="20px" />,
        showTab: true,
      },
      {
        id: 1,
        label: t("Simulations"),
        icon: <VRIcon height="20px" width="20px" />,
        showTab: canUseAnySimulations(clinicInfo?.licensing),
      },
      {
        id: 2,
        label: t("Videos"),
        icon: <PlayIcon height="20px" width="20px" />,
        showTab: clinicInfo?.licensing?.canPlayVideos,
      },
    ],
    [t, clinicInfo?.licensing]
  );

  // showTab is false for licensing-gated tabs the clinic can't use
  const activeTabs = useMemo(() => tabs.filter((tab) => tab.showTab), [tabs]);

  const shouldDisableSave = useMemo(
    () => Boolean(!selectedExamCards?.length) || scheduledError || isLoading,
    [selectedExamCards, scheduledError, isLoading]
  );

  const handleChangeDate = useCallback(
    (newValue: DateTime | null) => {
      const timezoneToUse = clinicInfo?.timezone ?? DateTime.local().zoneName;

      // Set the new value to the correct timezone and start of the day
      const newValueInZone = newValue?.setZone(timezoneToUse)?.startOf("day");

      // Set local time to the start of the day
      const today = DateTime.now().setZone(timezoneToUse).startOf("day");

      if (
        newValueInZone &&
        newValueInZone?.isValid &&
        newValueInZone >= today
      ) {
        setScheduledError(false);
        setScheduledDate(newValueInZone);
      } else {
        setScheduledError(true);
      }
    },
    [setScheduledDate, setScheduledError, clinicInfo?.timezone]
  );

  const handleChangeDevice = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = unlinkedDeviceOptions.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        setSelectedDevice({ ...value });
      }
    },
    [unlinkedDeviceOptions, setSelectedDevice]
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={getLocale(currentLang).locale}
    >
      <DefaultDrawer
        key={title}
        isOpen={isOpen}
        onClose={() => {
          setSelectedTab(0);
          onClose();
          resetValues();
        }}
        showCloseButton={false}
        title={title}
        customStyles={{
          width: { xs: "100%", md: "80%" },
          height: "100%",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "#5C6076",
            backgroundImage: "none",
          }),
        }}
        extraHeaderButtons={[
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              flexDirection: "row",
            }}
          >
            <CustomIconButton
              variant="light"
              icon={<CloseIcon />}
              isDisabled={isLoading}
              onClick={() => {
                setSelectedTab(0);
                onClose();
                resetValues();
              }}
            />

            <Button
              disabled={shouldDisableSave}
              sx={ModalCustomConfirm}
              onClick={() => {
                setSelectedTab(0);
                onConfirm();
                resetValues();
              }}
              fullWidth
            >
              {t("Save")}
            </Button>
          </Box>,
        ]}
      >
        <Box
          position="sticky"
          top={`${dimensions?.dynamicHeight ?? 59}px`}
          zIndex={100}
          display="flex"
          flexDirection="column"
          gap="8px"
          padding="8px 16px"
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? "#35394F" : surface,
            borderBottom: "1px solid #435B6D7A",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              gap: "8px",
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">
                {t("Scheduled Date")}
                <span style={{ color: getRequiredErrorColor(theme) }}>*</span>
              </Typography>
              <CustomDatePicker
                disablePast
                openTo="day"
                date={scheduledDate}
                handleChange={handleChangeDate}
                hasError={scheduledError}
              />
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography fontSize="12px">{t("Device Options")}</Typography>
              <SelectDropdown
                id="exam-slider-device-dropdown"
                disabled={shouldDisableDeviceOptions}
                value={selectedDevice?.displayName}
                dropdownOptions={unlinkedDeviceOptions}
                handleChange={handleChangeDevice}
              />
            </Box>
          </Box>

          {canUseExamPackaging && <PackageOrderSection />}
          {canUseExamPackaging && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "20px",
                margin: "0 -16px", // cancel the parent container's padding
                padding: "8px 16px 0px",
                borderTop:
                  theme.palette.mode === "dark"
                    ? "1px solid #5B6D7A"
                    : "1px solid #435B6D7A",
              }}
            >
              {activeTabs.map((tab) => {
                const isSelected = selectedTab === tab.id;
                const tabColor = isSelected ? "#E2772E" : onSurface;

                return (
                  <Button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    startIcon={tab.icon}
                    disableRipple
                    sx={{
                      minWidth: 0,
                      padding: 0,
                      color: tabColor,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: "#E2772E",
                      },
                    }}
                  >
                    <Typography
                      fontSize="12px"
                      fontWeight={600}
                      sx={{
                        textTransform: "uppercase",
                      }}
                    >
                      {tab.label}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>

        <Grid
          item
          display="flex"
          flexDirection="column"
          padding="16px"
          gap="16px"
        >
          {selectedTab === 0 && <ExamSliderList />}
          {selectedTab === 1 && <SimulationSliderList />}
          {selectedTab === 2 && <VideoSliderList />}
        </Grid>
      </DefaultDrawer>
    </LocalizationProvider>
  );
};

export default ExamSlider;
